import React, { useState } from 'react';
import { CSSTransition } from 'react-transition-group'
import { useSelector } from 'react-redux';
import { currentUserSelector } from "store/current_user";
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  colors
} from '@material-ui/core';
import PictureAsPdf from '@material-ui/icons/PictureAsPdf';
import Description from '@material-ui/icons/Description';
import {
  updateAppliesStatusCompleted,
  addTodayCallHistory,
} from 'api/search/core';
import { pdfExport } from 'api/pdfExport';
import { exportCsv } from 'api/csvExport';
import AsyncButton from 'utils/AsyncButton';
import {
  RESPONSE_STATUS_VALUES,
  UPDATE_SUCCESS_MESSAGE,
  DEFAULT_ERROR_MESSAGE,
  IS_SMS_SENDING_REQUIRED,
  CANCELLATION_CATEGORY,
} from 'const';
import MaybeLoading from 'utils/MaybeLoading';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { getIdentifiesStateLabel, convertDateSeparator, getFeeCategoryLabel } from 'utils/utils';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: 0,
  },
  inner: {
    minWidth: 1050,
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  actions: {
    backgroundColor: '#fafafa',
    position: 'relative',
    height: '60px',
    borderBottom: '1px solid #eeeeee',
  },
  pagination: {
    position: 'absolute',
    right: 0,
  },
  tableRow: {
    padding: '12px',
    whiteSpace: 'pre-wrap',
  },
  table: { padding: '12px' },
  buttonArea: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: '#fafafa',
    height: '60px',
    borderBottom: '1px solid #eeeeee',
  },
  leftButtonArea: { position: 'absolute', left: 0, marginLeft: 15 },
  rightButtonArea: { position: 'absolute', right: 0, marginRight: 15 },
  button: { marginRight: 5, marginLeft: 5 },
  tableTopButton: { marginRight: 5 },
  linkToDetail: {
    // 管理画面の共通CSSでtext-decoration:none;が指定されていて下線が表示されないため上書きしている
    textDecoration: 'underline',
    color: 'inherit',
    cursor: 'pointer',
    '&:hover': {
      color: colors.grey[600]
    }
  },

  arrowLeft: {
    cursor: 'pointer',
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderLeft: '2px solid #000000DB',
    borderBottom: '2px solid #000000DB',
    transform: 'rotate(45deg)',
    marginRight: '1.5rem',
  },

  /**
   * アニメーション関連
   */
  fadeAppear: {
    display: 'none',
  },

  fadeAppearDone: {
    display: 'none',
  },

  fadeEnter: {
    display: 'none',
  },

  fadeEnterDone: {
    display: 'none',
  },

  fadeExit: {
    opacity: '0',
    transition: 'all 0.5s ease',
    transform: 'translateX(100%)',
  },

  fadeExitDone: {
    opacity: '1',
    transition: 'all 0.5s ease',
    transform: 'translateX(0%)',
  },

  arrowEnter: {
    transition: 'all 0.5s ease',
    transform: 'rotate(225deg)',
  },

  arrowEnterDone: {
    transition: 'all 0.5s ease',
    transform: 'rotate(405deg)',
  },

  arrowExitDone: {
    transition: 'all 0.5s ease',
    transform: 'rotate(225deg)',
  },

  }));

const theme = createMuiTheme({
  overrides: {
    MuiTableCell: {
      root: {
        fontSize: '12px',
        paddingRight: '4px',
        paddingLeft: '4px',
        lineHeight: '1.2',
      },
    },
  },
})

const SearchTable = (props) => {
  const {
    className,
    applies,
    loading,
    totalCount,
    query,
    setQuery,
    onClickDetail,
    onClickSearch,
    setShouldSearchTriggered,
    ...rest
  } = props;

  const classes = useStyles();
  const [selectedApplies, setSelectedApplies] = useState([]);
  const [isUpdateStatusButtonLoading, setIsUpdateStatusButtonLoading] = useState(false);
  const [isPdfExportButtonLoading, setIsPdfExportButtonLoading] = useState(false);
  const [isAddTodayCallHistoryButtonLoading, setIsAddTodayCallHistoryButtonLoading] = useState(false);
  const [isCsvExportButtonLoading, setIsCsvExportButtonLoading] = useState(false);

  // コール指定日時・最終コール日・ロック者の折り畳みに使用
  const [hasOpened, setHasOpened] = useState(false);
  const handleArrowClick = () => setHasOpened((prev) => !prev);

  // 折り畳みアニメーションで使用
  const TIMEOUT = 100;
  const TRANSITION_CLASSES = {
    ARROW: {
      enter: classes.arrowEnter,
      enterDone: classes.arrowEnterDone,
      exitDone: classes.arrowExitDone,
    },
    TABLE_HEADER: {
      appearDone: classes.fadeAppearDone,
      enter: classes.fadeEnter,
      enterDone: classes.fadeEnterDone,
      exit: classes.fadeExit,
      exitDone: classes.fadeExitDone,
    },
    TABLE_BODY: {
      appear: classes.fadeAppear,
      appearDone: classes.fadeAppearDone,
      enter: classes.fadeEnter,
      enterDone: classes.fadeEnterDone,
      exit: classes.fadeExit,
      exitDone: classes.fadeExitDone,
    },
  };

  const currentUser = useSelector(currentUserSelector);
  const isNotSupervisor = !currentUser?.groupName.includes('supervisor') ?? true;

  const getChangeTypesLabel = (apply) => {
    let result = ''
    if (apply.address_change_id) {
      result += '住所\n'
    }
    if (apply.syoken_id) {
      result += '証券\n'
    }
    if (apply.vehicle_id) {
      result += '車両入替\n'
    }
    if (apply.nenrei_id) {
      result += '年齢・限定\n'
    }
    if (apply.purpose_id) {
      result += '使用目的\n'
    }
    if (apply.payment_id) {
      if (apply.new_payment === 2) {
        result += 'クレカ\n'
      } else if (apply.new_payment === 1) {
        if (apply.preferred_procedure === 2) {
          result += '口座（書面）\n'
        } else if (apply.preferred_procedure === 1) {
          result += '口座（ネット）\n'
        }
      }
    }
    if ((apply.insurance_type === 2 || apply.insurance_type === 3) && apply.cancellation_id) {
      result += '解約\n'
    }
    if (apply.insurance_type === 1 && apply.cancellation_id && (apply.is_certificate_required === 2 || apply.is_certificate_required === null)) {
      result += '単独解約\n'
    }
    if (apply.insurance_type === 1 && apply.cancellation_id && apply.is_certificate_required === 1) {
      result += '中断解約\n'
    }
    return result
  }
  const handlePageChange = (event, page) => {
    setQuery({
      ...query,
      page: page,
    });
    setShouldSearchTriggered(true)
  };

  const handleRowsPerPageChange = (event) => {
    setQuery(prev => {
      return {
        ...prev,
        pageSize: event.target.value,
        page: 0,
      }
    });
    setShouldSearchTriggered(true)
  };

  const handleSelectAll = (event) => {
    let selectedApplies;

    if (event.target.checked) {
      selectedApplies = applies.map((apply) => apply.apply_id);
    } else {
      selectedApplies = [];
    }

    setSelectedApplies(selectedApplies);
  };

  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedApplies.indexOf(id);
    let newSelectedApplies = [];

    if (selectedIndex === -1) {
      newSelectedApplies = newSelectedApplies.concat(selectedApplies, id);
    } else if (selectedIndex === 0) {
      newSelectedApplies = newSelectedApplies.concat(selectedApplies.slice(1));
    } else if (selectedIndex === selectedApplies.length - 1) {
      newSelectedApplies = newSelectedApplies.concat(
        selectedApplies.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedApplies = newSelectedApplies.concat(
        selectedApplies.slice(0, selectedIndex),
        selectedApplies.slice(selectedIndex + 1)
      );
    }

    setSelectedApplies(newSelectedApplies);
  };

  const createSortHandler = (property) => () => {
    const isAsc = query.orderBy.field === property && query.orderDirection === 'asc';
    setQuery({
      ...query,
      page: 0,
      orderBy: {
        field: property
      },
      orderDirection: isAsc ? 'desc' : 'asc'
    })
    setShouldSearchTriggered(true)
  };

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardActions className={classes.actions}>
        <TablePagination
          className={classes.pagination}
          component='div'
          count={totalCount}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          page={query.page}
          rowsPerPage={query.pageSize}
          rowsPerPageOptions={[10, 20, 50, 100, 200]}
        />
      </CardActions>
      <CardActions className={classes.buttonArea}>
        <div className={classes.rightButtonArea}>
          <AsyncButton
            className={clsx('csvExportButton', classes.tableTopButton)}
            type='button'
            variant='outlined'
            color='primary'
            size='small'
            startIcon={<Description />}
            onClick={async () => {
              setIsCsvExportButtonLoading(true);
              await exportCsv(selectedApplies)
                .then(res => {
                  setSelectedApplies([]);
                  // 更新した値を表示するために再読み込み
                  onClickSearch();
                })
                .catch(e => {
                  console.error(e)
                })
              setIsCsvExportButtonLoading(false);
            }}
            disabled={
              loading || applies.length === 0 || selectedApplies.length === 0 || isNotSupervisor
            }
            loading={isCsvExportButtonLoading}
          >
            選択した項目をCSV出力
          </AsyncButton>
          <AsyncButton
            className={clsx('pdfExportButton', classes.tableTopButton)}
            type='button'
            variant='outlined'
            color='primary'
            size='small'
            startIcon={<PictureAsPdf />}
            onClick={async () => {
              setIsPdfExportButtonLoading(true);
              await pdfExport(selectedApplies)
                .then((resp) => {
                  if (resp.status === 'success') {
                    setIsPdfExportButtonLoading(false);
                    setSelectedApplies([]);
                    // 更新した値を表示するために再読み込み
                    onClickSearch();
                  } else {
                    setIsPdfExportButtonLoading(false);
                  }
                })
                .catch((e) => {
                  setIsPdfExportButtonLoading(false);
                });
              setIsPdfExportButtonLoading(false);
            }}
            disabled={
              loading || applies.length === 0 || selectedApplies.length === 0 || isNotSupervisor
            }
            loading={isPdfExportButtonLoading}
          >
            選択した項目を帳票出力
          </AsyncButton>
        </div>
      </CardActions>
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <ThemeProvider theme={theme}>
              <Table p={15}>
                <colgroup>

                  {/* NOTE: 列を折りたたんだ際に画面幅も一緒に更新する必要があるため */}
                  {hasOpened ? (
                    <>
                      <col width='1%' />{/* チェック */}
                      <col width='5%' />{/* 連番 */}
                      <col width='8%' />{/* 受付日時 */}
                      <col width='10%'/>{/* 契約者名 */}
                      <col width='7%' />{/* 証券番号 */}
                      <col width='7%' />{/* 変更内容 */}
                      <col width='6%' />{/* 本人確認 */}
                      <col width='7%' />{/* フィー区分 */}
                      <col width='7%' />{/* 解約区分 */}
                      <col width='5%' />{/* LINE */}
                      <col width='5%' />{/* SMS */}
                      <col width='8%' />{/* コール指定日 */}
                      <col width='7%' />{/* 最終コール日 */}
                      <col width='7%' />{/* ロック者 */}
                      <col width='1%' />{/* 開閉ボタン */}
                      <col width='9%' />{/* ステータス */}
                    </>
                  ):(
                    <>
                      <col width='1%' />{/* チェック */}
                      <col width='6%' />{/* 連番 */}
                      <col width='12%' />{/* 受付日時 */}
                      <col width='15%'/>{/* 契約者名 */}
                      <col width='9%' />{/* 証券番号 */}
                      <col width='9%' />{/* 変更内容 */}
                      <col width='8%' />{/* 本人確認 */}
                      <col width='9%' />{/* フィー区分 */}
                      <col width='9%' />{/* 解約区分 */}
                      <col width='6%' />{/* LINE */}
                      <col width='6%' />{/* SMS */}
                      <col width='1%' />{/* 開閉ボタン */}
                      <col width='9%' />{/* ステータス */}
                    </>
                  )}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        checked={
                          !loading &&
                          applies.length !== 0 &&
                          selectedApplies.length === applies.length
                        }
                        color='primary'
                        indeterminate={
                          selectedApplies.length > 0 &&
                          selectedApplies.length < applies.length
                        }
                        className='tableRowCheckbox'
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={query.orderBy.field === 'id'}
                        direction={query.orderDirection}
                        onClick={createSortHandler('id')}
                      >
                        連番
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>受付日時</TableCell>
                    <TableCell>契約者名</TableCell>
                    <TableCell>証券番号</TableCell>
                    <TableCell>変更内容</TableCell>
                    <TableCell>本人確認</TableCell>
                    <TableCell>フィー区分</TableCell>
                    <TableCell>解約区分</TableCell>
                    <TableCell>LINE</TableCell>
                    <TableCell>SMS</TableCell>
                    <CSSTransition
                      in={!hasOpened}
                      timeout={TIMEOUT}
                      classNames={TRANSITION_CLASSES.TABLE_HEADER}
                      appear={true}
                    >
                      <TableCell>コール指定日</TableCell>
                    </CSSTransition>

                    <CSSTransition
                      in={!hasOpened}
                      timeout={TIMEOUT}
                      classNames={TRANSITION_CLASSES.TABLE_HEADER}
                      appear={true}
                    >
                      <TableCell>最終コール日</TableCell>
                    </CSSTransition>
                    <CSSTransition
                      in={!hasOpened}
                      timeout={TIMEOUT}
                      classNames={TRANSITION_CLASSES.TABLE_HEADER}
                      appear={true}
                    >
                      <TableCell>ロック者</TableCell>
                    </CSSTransition>

                    <TableCell onClick={handleArrowClick}>
                      <CSSTransition
                        in={!hasOpened}
                        timeout={TIMEOUT}
                        classNames={TRANSITION_CLASSES.ARROW}>
                        <div className={classes.arrowLeft} />
                      </CSSTransition>
                    </TableCell>
                    <TableCell>ステータス</TableCell>
                  </TableRow>
                </TableHead>
                <MaybeLoading
                  loading={loading}
                  progressComponent={
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={10}>
                          <Grid container alignItems='center' justify='center'>
                            <Grid item>
                              <CircularProgress />
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  }
                >
                  <TableBody>
                    {applies.map((apply) => (
                      <TableRow
                        className={classes.tableRow}
                        key={apply.apply_id}
                        selected={selectedApplies.indexOf(apply.apply_id) !== -1}
                      >
                        <TableCell>
                          <Checkbox
                            checked={
                              selectedApplies.indexOf(apply.apply_id) !== -1
                            }
                            color='primary'
                            onChange={(event) =>
                              handleSelectOne(event, apply.apply_id)
                            }
                            value='true'
                          />
                        </TableCell>
                        <TableCell>
                          <span
                            className={classes.linkToDetail}
                            onClick={() => onClickDetail(apply)}
                          >
                            {apply.apply_id}
                          </span>
                        </TableCell>
                        <TableCell>
                          {convertDateSeparator(apply.created_at)}
                        </TableCell>
                        <TableCell>
                          {apply.name_revised ? apply.name_revised : apply.name}
                        </TableCell>
                        <TableCell>
                          {apply.policy_number_revised ? apply.policy_number_revised : apply.policy_number}
                        </TableCell>
                        <TableCell>
                          {getChangeTypesLabel(apply)}
                        </TableCell>
                        <TableCell>
                          {getIdentifiesStateLabel(apply.is_identified_all)}
                        </TableCell>
                        <TableCell>
                          {getFeeCategoryLabel(apply.fee_category)}
                        </TableCell>
                        <TableCell>
                          {CANCELLATION_CATEGORY.find(({_, value}) => value === apply.cancellation_category)?.label}
                        </TableCell>
                        <TableCell>
                          {apply.line_id ? '可' : '不可'}
                        </TableCell>
                        <TableCell>
                          {apply.is_sms_sending_required === IS_SMS_SENDING_REQUIRED['要'] ? '要' : ''}
                        </TableCell>

                        <CSSTransition
                          in={!hasOpened}
                          timeout={TIMEOUT}
                          classNames={TRANSITION_CLASSES.TABLE_BODY}
                          appear={true}
                          >
                          <TableCell>
                            {convertDateSeparator(apply.call_designated_date)}
                          </TableCell>
                        </CSSTransition>

                      <CSSTransition
                        in={!hasOpened}
                        timeout={TIMEOUT}
                        classNames={TRANSITION_CLASSES.TABLE_BODY}
                        appear={true}
                        >
                        <TableCell>
                          {convertDateSeparator(apply.outbound_date)}
                        </TableCell>
                      </CSSTransition>

                      <CSSTransition
                        in={!hasOpened}
                        timeout={TIMEOUT}
                        classNames={TRANSITION_CLASSES.TABLE_BODY}
                        appear={true}
                        >
                        <TableCell>
                          {apply.locked_by_name}
                        </TableCell>
                      </CSSTransition>
                        <TableCell>
                           &nbsp; {/* 矢印用の空白 */}
                        </TableCell>
                        <TableCell>
                          {RESPONSE_STATUS_VALUES[apply.response_status]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </MaybeLoading>
              </Table>
            </ThemeProvider>
          </div>
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          className={classes.pagination}
          component='div'
          count={totalCount}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          page={query.page}
          rowsPerPage={query.pageSize}
          rowsPerPageOptions={[10, 20, 50, 100, 200]}
        />
      </CardActions>
      <CardActions className={classes.buttonArea}>
        <div className={classes.rightButtonArea}>
          <AsyncButton
            className={clsx('updateStatusButton', classes.button)}
            color='secondary'
            variant='contained'
            onClick={async () => {
              setIsUpdateStatusButtonLoading(true);
              await updateAppliesStatusCompleted(selectedApplies)
                .then((resp) => {
                  if (resp.status === 'success') {
                    setIsUpdateStatusButtonLoading(false);
                    setSelectedApplies([]);
                    // 更新した値を表示するために再読み込み
                    onClickSearch();
                    alert(UPDATE_SUCCESS_MESSAGE);
                  } else {
                    setIsUpdateStatusButtonLoading(false);
                    alert(DEFAULT_ERROR_MESSAGE);
                  }
                })
                .catch((e) => {
                  setIsUpdateStatusButtonLoading(false);
                  alert(DEFAULT_ERROR_MESSAGE);
                });
            }}
            disabled={
              loading || applies.length === 0 || selectedApplies.length === 0 || isNotSupervisor
            }
            loading={isUpdateStatusButtonLoading}
          >
            選択した項目を完了にする
          </AsyncButton>
          <AsyncButton
            className={clsx('addTodayCallHistoryButton', classes.button)}
            color='primary'
            variant='contained'
            onClick={async () => {
              setIsAddTodayCallHistoryButtonLoading(true);
              await addTodayCallHistory(selectedApplies)
                .then((resp) => {
                  if (resp.status === 'success') {
                    setIsAddTodayCallHistoryButtonLoading(false);
                    setSelectedApplies([]);
                    // 更新した値を表示するために再読み込み
                    onClickSearch();
                    alert(UPDATE_SUCCESS_MESSAGE);
                  } else {
                    setIsAddTodayCallHistoryButtonLoading(false);
                    alert(DEFAULT_ERROR_MESSAGE);
                  }
                })
                .catch((e) => {
                  setIsAddTodayCallHistoryButtonLoading(false);
                  alert(DEFAULT_ERROR_MESSAGE);
                });
            }}
            disabled={
              loading || applies.length === 0 || selectedApplies.length === 0 || isNotSupervisor
            }
            loading={isAddTodayCallHistoryButtonLoading}
          >
            選択した項目にコール日を入力する
          </AsyncButton>
        </div>
      </CardActions>
    </Card>
  );
};

SearchTable.propTypes = {
  className: PropTypes.string,
  applies: PropTypes.array.isRequired,
};

export default SearchTable;
